import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Card, Button, Table, Spinner, Badge, OverlayTrigger, Tooltip, Dropdown } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { KTIcon } from '../../../../_metronic/helpers'
import { ConfirmationModal } from '../../components/ConfirmationModal'
import { GET_TWILIO_WHATSAPP_TEMPLATES } from '../../gql/queries/twilio'
import { TOGGLE_TWILIO_TEMPLATE_ACTIVE, ARCHIVE_TWILIO_TEMPLATE, DELETE_TWILIO_TEMPLATE, SUBMIT_TWILIO_TEMPLATE_FOR_APPROVAL } from '../../gql/mutations/twilio'
import { TwilioSyncDiffModal } from './TwilioSyncDiffModal'

import { TemplateConfigurationModal } from './TemplateConfigurationModal'
import { CreateTwilioTemplateModal } from './CreateTwilioTemplateModal'

export const TwilioTemplatesView = () => {
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showConfigModal, setShowConfigModal] = useState<any>(null)

  // Confirmation modal states
  const [confirmAction, setConfirmAction] = useState<{
    type: 'toggle' | 'archive' | 'delete' | 'submit' | null
    template: any
  }>({ type: null, template: null })
  const [confirmLoading, setConfirmLoading] = useState(false)

  const { data, loading, refetch } = useQuery(GET_TWILIO_WHATSAPP_TEMPLATES, {
    fetchPolicy: 'cache-and-network',
    onError: (err) => toast.error(err.message || 'Error loading Twilio templates'),
  })

  // Mutations
  const [toggleActive] = useMutation(TOGGLE_TWILIO_TEMPLATE_ACTIVE)
  const [archiveTemplate] = useMutation(ARCHIVE_TWILIO_TEMPLATE)
  const [deleteTemplate] = useMutation(DELETE_TWILIO_TEMPLATE)
  const [submitForApproval] = useMutation(SUBMIT_TWILIO_TEMPLATE_FOR_APPROVAL)

  const templatesData = useMemo(() => data?.twilioWhatsappTemplates || [], [data])

  const handleToggleActive = (template: any) => {
    setConfirmAction({ type: 'toggle', template })
  }

  const handleArchive = (template: any) => {
    setConfirmAction({ type: 'archive', template })
  }

  const handleDelete = (template: any) => {
    setConfirmAction({ type: 'delete', template })
  }

  const handleSubmitForApproval = (template: any) => {
    setConfirmAction({ type: 'submit', template })
  }

  const executeConfirmAction = async () => {
    if (!confirmAction.type || !confirmAction.template) return

    setConfirmLoading(true)
    try {
      const template = confirmAction.template

      if (confirmAction.type === 'toggle') {
        const { data: res } = await toggleActive({
          variables: { contentSid: template.contentSid, isActive: !template.isActive },
          refetchQueries: ['TwilioWhatsappTemplates'],
          awaitRefetchQueries: true
        })
        if (res?.toggleTwilioTemplateActive?.success) {
          toast.success(res.toggleTwilioTemplateActive.message)
        } else {
          toast.error('Failed to change status')
        }
      } else if (confirmAction.type === 'archive') {
        const { data: res } = await archiveTemplate({
          variables: { contentSid: template.contentSid },
          refetchQueries: ['TwilioWhatsappTemplates'],
          awaitRefetchQueries: true
        })
        if (res?.archiveTwilioTemplate?.success) {
          toast.success(res.archiveTwilioTemplate.message)
        } else {
          toast.error('Failed to archive template')
        }
      } else if (confirmAction.type === 'delete') {
        const { data: res } = await deleteTemplate({
          variables: { contentSid: template.contentSid },
          refetchQueries: ['TwilioWhatsappTemplates'],
          awaitRefetchQueries: true
        })
        if (res?.deleteTwilioTemplate?.success) {
          toast.success(res.deleteTwilioTemplate.message)
        } else {
          toast.error('Failed to delete template')
        }
      } else if (confirmAction.type === 'submit') {
        const { data: res } = await submitForApproval({
          variables: { contentSid: template.contentSid },
          refetchQueries: ['TwilioWhatsappTemplates'],
          awaitRefetchQueries: true
        })
        if (res?.submitTwilioTemplateForApproval?.success) {
          toast.success(res.submitTwilioTemplateForApproval.message)
        } else {
          toast.error('Failed to submit template for approval')
        }
      }
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setConfirmLoading(false)
      setConfirmAction({ type: null, template: null })
    }
  }

  if (loading && templatesData.length === 0) {
    return (
      <div className="card">
        <div className="card-body py-10 text-center">
          <Spinner animation="border" />
          <div className="mt-2 text-muted">Loading Twilio templates...</div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-5 mt-5">
        <div>
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bolder fs-3 mb-1">
              Twilio WhatsApp Templates
            </span>
          </h3>
          <span className="text-muted fw-bold fs-7">
            {templatesData.length} synced templates
          </span>
        </div>
        <div className="d-flex gx-2 gap-2">
          <Button variant="primary" size="sm" onClick={() => setShowSyncModal(true)}>
            <i className="bi bi-arrow-repeat me-1" />
            Sync (Twilio API)
          </Button>
          <Button variant="success" size="sm" onClick={() => setShowCreateModal(true)}>
            <i className="bi bi-plus me-1" />
            Create Template
          </Button>
        </div>
      </div>

      <Card>
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle table-row-dashed fs-6 gy-5 mb-0 px-2 border">
            <thead>
              <tr className="text-start text-muted fw-bolder fs-7 text-uppercase gs-0">
                <th className="min-w-200px px-4">Local Name (Twilio SID)</th>
                <th className="min-w-100px">Lang / Category</th>
                <th className="min-w-100px text-center">WhatsApp Mgmt Status</th>
                <th className="min-w-100px text-center">AI (LLM Agent)</th>
                <th className="min-w-100px text-center">PolicyTools Status</th>
                <th className="text-end min-w-100px px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templatesData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-muted">
                    No templates found. Click "Sync" to grab existing templates from Twilio.
                  </td>
                </tr>
              ) : (
                templatesData.map((t: any) => (
                  <tr key={t.contentSid} className={t.isArchived ? 'opacity-50' : ''}>
                    <td className="px-4">
                      <div className="d-flex align-items-center">
                        <div className="d-flex justify-content-start flex-column">
                          <span className="text-dark fw-bolder text-hover-primary mb-1 fs-6">
                            {t.friendlyName}
                          </span>
                          <span className="text-muted fw-bold text-muted d-block fs-7">
                            <code>{t.contentSid}</code>
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="text-dark fw-bolder d-block fs-7">{t.language}</span>
                      <span className="text-muted fw-bold d-block fs-7">
                        {t.contentType || 'twilio/text'}
                      </span>
                    </td>
                    <td className="text-center">
                      <span 
                        className={`badge badge-light-${
                          t.approvalStatus === 'approved' ? 'success' : 
                          t.approvalStatus === 'rejected' ? 'danger' : 'warning'
                        } fw-bold text-uppercase`}
                      >
                        {t.approvalStatus}
                      </span>
                    </td>
                    <td className="text-center">
                        <span className={`badge badge-light-${t.llmInvocable ? 'success' : 'secondary'} rounded-circle p-2`}>
                          <i className={`bi ${t.llmInvocable ? 'bi-robot text-success' : 'bi-x text-muted'} fs-5`}></i>
                        </span>
                    </td>
                    <td className="text-center">
                      {t.isArchived ? (
                        <span className="badge badge-secondary">Archived</span>
                      ) : (
                        <span className={`badge badge-light-${t.isActive ? 'primary' : 'warning'} fw-bold`}>
                          {t.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td className="text-end px-4">
                      <Dropdown>
                        <Dropdown.Toggle variant="light" size="sm" className="btn-active-light-primary">
                          Actions
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end" popperConfig={{ strategy: 'fixed' }}>
                          <Dropdown.Item onClick={() => setShowConfigModal(t)}>
                            <KTIcon iconName="setting-2" className="me-2 text-primary" /> Configuration (AI/Vars)
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          {!t.isArchived && t.approvalStatus !== 'approved' && (
                            <Dropdown.Item onClick={() => handleSubmitForApproval(t)} className="text-success">
                              <KTIcon iconName="send" className="me-2 text-success" /> Submit for WhatsApp Approval
                            </Dropdown.Item>
                          )}
                          {!t.isArchived && (
                            <Dropdown.Item onClick={() => handleToggleActive(t)}>
                              <KTIcon iconName={t.isActive ? "cross" : "check"} className="me-2" />
                              {t.isActive ? 'Deactivate' : 'Activate'}
                            </Dropdown.Item>
                          )}
                          {!t.isArchived && (
                            <Dropdown.Item onClick={() => handleArchive(t)} className="text-warning">
                              <KTIcon iconName="archive" className="me-2 text-warning" /> Archive
                            </Dropdown.Item>
                          )}
                          <Dropdown.Item onClick={() => handleDelete(t)} className="text-danger">
                            <KTIcon iconName="trash" className="me-2 text-danger" /> Delete (Local Only)
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <TwilioSyncDiffModal 
        show={showSyncModal} 
        onHide={() => setShowSyncModal(false)} 
        onSuccess={() => refetch()} 
      />

      <CreateTwilioTemplateModal 
        show={showCreateModal} 
        onHide={() => setShowCreateModal(false)} 
        onSuccess={() => refetch()} 
      />

      {showConfigModal && (
        <TemplateConfigurationModal
          show={!!showConfigModal}
          onHide={() => setShowConfigModal(null)}
          template={showConfigModal}
          onSuccess={() => refetch()}
          isTwilio={true}
        />
      )}

      <ConfirmationModal
        show={confirmAction.type !== null}
        onHide={() => setConfirmAction({ type: null, template: null })}
        onConfirm={executeConfirmAction}
        title={
          confirmAction.type === 'toggle'
            ? `${confirmAction.template?.isActive ? 'Deactivate' : 'Activate'} Template`
            : confirmAction.type === 'archive'
            ? 'Archive Template'
            : confirmAction.type === 'delete'
            ? 'Delete Template'
            : confirmAction.type === 'submit'
            ? 'Submit for Approval'
            : 'Confirm Action'
        }
        message={
          confirmAction.type === 'toggle' ? (
            <>
              <p>
                Are you sure you want to <strong>{confirmAction.template?.isActive ? 'deactivate' : 'activate'}</strong> the
                template <strong>"{confirmAction.template?.friendlyName}"</strong>?
              </p>
            </>
          ) : confirmAction.type === 'archive' ? (
            <>
              <p>
                Are you sure you want to <strong>archive</strong> the template{' '}
                <strong>"{confirmAction.template?.friendlyName}"</strong>?
              </p>
              <p className='text-warning small mb-0'>
                <i className='bi bi-exclamation-triangle me-2'></i>It will become inactive and cannot be restored from
                the UI.
              </p>
            </>
          ) : confirmAction.type === 'delete' ? (
            <>
              <p>
                <strong className='text-danger'>WARNING!</strong> Permanently delete{' '}
                <strong>"{confirmAction.template?.friendlyName}"</strong> from the local database?
              </p>
              <p className='text-muted small mb-0'>This action cannot be undone.</p>
            </>
          ) : confirmAction.type === 'submit' ? (
            <>
              <p>
                Submit <strong>"{confirmAction.template?.friendlyName}"</strong> for WhatsApp Business approval?
              </p>
              <p className='text-info small mb-0'>
                <i className='bi bi-info-circle me-2'></i>This will submit the template to Meta/WhatsApp for review.
                Status will update to "pending".
              </p>
            </>
          ) : (
            ''
          )
        }
        confirmText={
          confirmAction.type === 'toggle'
            ? confirmAction.template?.isActive
              ? 'Deactivate'
              : 'Activate'
            : confirmAction.type === 'archive'
            ? 'Archive'
            : confirmAction.type === 'delete'
            ? 'Delete'
            : confirmAction.type === 'submit'
            ? 'Submit'
            : 'Confirm'
        }
        variant={
          confirmAction.type === 'delete'
            ? 'danger'
            : confirmAction.type === 'submit'
            ? 'success'
            : confirmAction.type === 'archive'
            ? 'warning'
            : 'primary'
        }
        loading={confirmLoading}
      />
    </>
  )
}
